import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shadcn/ui/card";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { reaction } from "mobx";
import { type FC, memo, useEffect } from "react";
import AuthService, { AuthenticationStatus } from "@/src/application/services/auth-service";
import { useBrand } from "@/src/brand";
import { injectViewmodel } from "@/src/infrastructure/di";
import Logo from "@/src/presentation/components/logo";
import ViewmodelWrapper from "@/src/presentation/components/viewmodel-wrapper";
import { useInject } from "@/src/presentation/hooks/useInject";
import LoginViewModel, { AuthStatus } from "@/src/presentation/view-models/login-viewmodel";
import LoginForm from "./_components/login-form";
import RegisterButton from "./_components/register-button";

interface LoginSearch {
  redirect?: string;
}

const BaseState: FC<{ model: LoginViewModel; redirectUrl?: string }> = ({ model, redirectUrl }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const dispose = reaction(
      () => model.authStatus,
      (status) => {
        if (status === AuthStatus.Success) {
          const targetUrl = redirectUrl || "/dashboard/devices";
          navigate({ to: targetUrl, replace: true });
        }
      }
    );
    return () => dispose();
  }, [model.authStatus, model.__currentVersion, redirectUrl, navigate]);

  return null;
};

const State = memo(injectViewmodel(LoginViewModel, BaseState));

const LoginPage: FC = () => {
  const authService = useInject<AuthService>(AuthService);
  const { redirect } = useSearch({ from: "/_public/login/" });
  const navigate = useNavigate();
  const brand = useBrand();

  // Redirect if already authenticated
  useEffect(() => {
    if (authService.status === AuthenticationStatus.Authenticated) {
      navigate({ to: redirect || "/dashboard/devices", replace: true });
    }
  }, [authService.status, redirect, navigate]);

  return (
    <ViewmodelWrapper type={LoginViewModel}>
      <State redirectUrl={redirect} />
      <div
        className="flex min-h-screen items-center justify-center bg-slate-50 p-4"
        style={
          brand.loginBg
            ? {
                backgroundImage: "var(--login-bg-image)",
                backgroundPosition: "center",
                backgroundSize: "cover",
              }
            : undefined
        }
        data-testid="login-page-background"
      >
        <div className="w-full max-w-md">
          <Logo />
          <Card className="border-brand-accent-subtle shadow-lg">
            <CardHeader className="space-y-1">
              <CardTitle className="text-center text-2xl font-bold">Login</CardTitle>
              <CardDescription className="text-center">
                Log in to access the water device management system
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LoginForm />
            </CardContent>
            <CardFooter className="flex flex-col gap-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card text-muted-foreground px-2">Or</span>
                </div>
              </div>
              <RegisterButton />
            </CardFooter>
          </Card>
        </div>
      </div>
    </ViewmodelWrapper>
  );
};

export const Route = createFileRoute("/_public/login/")({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    redirect: search.redirect as string | undefined,
  }),
  component: LoginPage,
});
